import axios from 'axios';
import { API_CONFIG } from '../config/api';
import { getAxiosErrorMessage } from './authService';

/**
 * Client for the internal (Cloudflare Access / VPN) API.
 *
 * Deliberately NOT `authApi`: the internal host identifies the caller through
 * Cloudflare Access, and the backend resolves identity token-first, so a wallet
 * Keycloak `Authorization: Bearer` on these requests would replace the Access
 * identity (which carries the staff email the internal wallet routes check)
 * and every call would come back "not allowed". This instance sends no Bearer,
 * sends cookies cross-origin so the `CF_Authorization` session is used, and
 * has no token-refresh interceptor, so an Access failure is never mistaken for
 * an expired wallet login.
 */
export const internalApi = axios.create({
  baseURL: API_CONFIG.INTERNAL_API_BASE_URL || undefined,
  withCredentials: true,
  headers: { 'Content-Type': 'application/json' },
});

/** Opening any internal URL top-level lets Cloudflare Access establish the session cookie for that host. */
export const INTERNAL_SIGN_IN_URL = API_CONFIG.INTERNAL_API_BASE_URL
  ? `${API_CONFIG.INTERNAL_API_BASE_URL}${API_CONFIG.ENDPOINTS.INTERNAL_WALLET_PING}`
  : '';

export function openInternalSignIn() {
  if (!INTERNAL_SIGN_IN_URL || typeof window === 'undefined') return;
  window.open(INTERNAL_SIGN_IN_URL, '_blank', 'noopener');
}

/**
 * True when the failure is about the Access session rather than the request:
 * a 401/403, or no response at all (the browser refused Access's cross-origin
 * login redirect, which surfaces as a network error).
 */
export function isInternalSessionError(err) {
  if (!err || typeof err !== 'object') return false;
  const status = err.response?.status;
  if (status === 401 || status === 403) return true;
  return err.isAxiosError === true && !err.response;
}

export const INTERNAL_SIGN_IN_HINT =
  'Sign in to the internal API (Cloudflare Access) in the tab that opens, then retry. If it does not open, check the VPN.';

/** Error text for an internal-API failure; session problems get the sign-in hint instead of a raw message. */
export function getInternalErrorMessage(err, fallback = 'Request failed') {
  if (isInternalSessionError(err)) return INTERNAL_SIGN_IN_HINT;
  return getAxiosErrorMessage(err, fallback);
}
